import { MAX_PLAYERS, MIN_PLAYERS } from './rules';
import type { CardData, GuessResult, PackSummary, PublicSubmission, RoomView, TeamView } from './types';
import { teamsOn, type RoomState } from './engine';

/** Shown in place of the dealt fallback prompt while someone writes their own. */
const WRITING_PLACEHOLDER: CardData = { id: 'writing', type: 'prompt', text: '___', pick: 1 };

/**
 * What one viewer is allowed to see. `viewerId` null is the TV/spectator view.
 * Hands, tokens, authorship of anonymous cards, votes in progress and guesses in progress never
 * leave the server.
 */
export function buildView(state: RoomState, viewerId: string | null, packs: PackSummary[], now: number): RoomView {
  const round = state.round;
  const roundOver = state.phase === 'round_over' || state.phase === 'game_over';
  const guessing = state.phase === 'guessing';
  const voting = state.settings.judgeMode === 'vote';
  const me = viewerId ? state.players.find((p) => p.id === viewerId) ?? null : null;
  const withTeams = teamsOn(state.settings);
  const guesses = round?.guesses ?? {};
  const teamOfId = (id: string) => state.players.find((p) => p.id === id)?.team ?? null;

  const players = state.players.map((p) => {
    let done = false;
    if (round && state.phase === 'submitting') done = round.submissions.some((x) => x.playerId === p.id);
    if (round && state.phase === 'judging' && voting) done = round.submissions.some((x) => x.votes.includes(p.id));
    if (round && guessing) done = Boolean(guesses[p.id]);
    return {
      id: p.id,
      name: p.name,
      avatar: p.avatar,
      score: p.score,
      isHost: p.id === state.hostId,
      isJudge: Boolean(round && !roundOver && round.judgeId === p.id),
      connected: p.connected,
      done,
      team: withTeams ? (p.team ?? null) : null,
    };
  });

  const teams: TeamView[] = withTeams
    ? state.settings.teams!.map((name, index) => ({
        index,
        name,
        score: state.teamScores?.[index] ?? 0,
        playerIds: state.players.filter((p) => p.team === index).map((p) => p.id),
      }))
    : [];

  let submissions: PublicSubmission[] = [];
  if (round) {
    const visible = state.phase === 'judging' || guessing || roundOver;
    submissions = visible
      ? round.submissions.map((sub) => ({
          id: sub.id,
          cards: sub.revealed || roundOver ? sub.cards : null,
          playerId: roundOver ? sub.playerId : null,
          playerName: roundOver ? sub.playerName : null,
          votes: roundOver && voting ? sub.votes.length : null,
          isWinner: (roundOver || guessing) && round.winnerSubmissionIds.includes(sub.id),
          mine: Boolean(me && sub.playerId === me.id),
          teammate: Boolean(voting && withTeams && me && sub.playerId !== me.id && me.team != null && teamOfId(sub.playerId) === me.team),
        }))
      : [];
  }

  let guessResults: GuessResult[] | null = null;
  if (round?.guessesRevealed && roundOver) {
    const authors = new Set(round.submissions.filter((x) => round.winnerSubmissionIds.includes(x.id)).map((x) => x.playerId));
    guessResults = Object.entries(guesses).flatMap(([playerId, suspectId]) => {
      const player = state.players.find((p) => p.id === playerId);
      return player ? [{ playerId, playerName: player.name, suspectId, correct: authors.has(suspectId) }] : [];
    });
  }

  const mySub = me && round ? round.submissions.find((x) => x.playerId === me.id) : undefined;
  const myVote = me && round ? round.submissions.find((x) => x.votes.includes(me.id)) : undefined;
  const writer = round?.writerId ? state.players.find((p) => p.id === round.writerId) : undefined;

  return {
    code: state.code,
    phase: state.phase,
    settings: state.settings,
    packs,
    players,
    hostId: state.hostId,
    round: round
      ? {
          number: round.number,
          judgeId: round.judgeId,
          prompt: state.phase === 'writing' ? WRITING_PLACEHOLDER : round.prompt,
          submissions,
          deadline: round.deadline,
          winnerPlayerIds: roundOver
            ? round.submissions.filter((x) => round.winnerSubmissionIds.includes(x.id)).map((x) => x.playerId)
            : [],
          notice: round.notice,
          worst: Boolean(round.worst),
          writerId: round.writerId ?? null,
          writtenBy: round.prompt.writeIn ? (writer?.name ?? null) : null,
          promptOptions: state.phase === 'choosing' ? (round.promptOptions ?? null) : null,
          suspects: guessing ? uniqueSuspects(round.submissions) : null,
          guessers: guessing ? (round.guessers ?? []) : [],
          guesses: guessResults,
        }
      : null,
    teams,
    recap: state.recap,
    me: me
      ? {
          id: me.id,
          name: me.name,
          hand: me.hand,
          submitted: Boolean(mySub),
          votedFor: myVote?.id ?? null,
          writeInsLeft: me.writeInsLeft,
          redrawsLeft: me.redrawsLeft,
          canGuess: Boolean(guessing && round?.guessers?.includes(me.id)),
          guessedFor: guessing || roundOver ? (guesses[me.id] ?? null) : null,
        }
      : null,
    minPlayers: MIN_PLAYERS,
    maxPlayers: MAX_PLAYERS,
    serverTime: now,
  };
}

/** Everyone who played a card, sorted by name: in card order the list would give the authors away. */
function uniqueSuspects(subs: { playerId: string; playerName: string }[]): { id: string; name: string }[] {
  const seen = new Map<string, string>();
  for (const s of subs) seen.set(s.playerId, s.playerName);
  return [...seen].map(([id, name]) => ({ id, name })).sort((a, b) => a.name.localeCompare(b.name));
}
